import { ChevronRight } from "lucide-react";
import React from "react";

import { VisitBulkSelectCheckbox } from "./VisitBulkSelectCheckbox";
import { ChronologyViewType, type MedChronTemplate } from "./types";
import { formatVisitType } from "./utils/visitFormatting";

interface VisitGroupHeaderProps {
  groupKey: string;
  groupLabel: string;
  visitCount: number;
  viewType: ChronologyViewType;
  template: MedChronTemplate | null;
  isExpanded: boolean;
  onToggle: () => void;
  showGroupBulkCheckbox?: boolean;
  groupBulkCheckboxChecked?: boolean;
  groupBulkCheckboxIndeterminate?: boolean;
  onGroupBulkCheckboxToggle?: () => void;
}

export const VisitGroupHeader: React.FC<VisitGroupHeaderProps> = ({
  groupKey,
  groupLabel,
  visitCount,
  viewType,
  template,
  isExpanded,
  onToggle,
  showGroupBulkCheckbox = false,
  groupBulkCheckboxChecked = false,
  groupBulkCheckboxIndeterminate = false,
  onGroupBulkCheckboxToggle,
}) => {
  const isCollapsible = viewType !== ChronologyViewType.DATE;

  const bulkCheckbox =
    showGroupBulkCheckbox && onGroupBulkCheckboxToggle ? (
      <div className="flex shrink-0 items-center" onClick={(e) => e.stopPropagation()}>
        <VisitBulkSelectCheckbox
          ariaLabel={`Select all visits in ${groupLabel}`}
          checked={groupBulkCheckboxChecked}
          indeterminate={groupBulkCheckboxIndeterminate}
          onToggle={onGroupBulkCheckboxToggle}
        />
      </div>
    ) : null;

  return (
    <div data-group-key={groupKey} className="mb-4 flex items-center gap-3">
      {bulkCheckbox}
      {isCollapsible ? (
        <button
          onClick={onToggle}
          className="group flex items-center gap-3 text-left transition-all hover:opacity-80"
        >
          <div
            className={`flex h-8 w-8 items-center justify-center rounded-lg bg-neutral-100 text-neutral-500 transition-transform duration-200 ${isExpanded ? "rotate-90" : ""}`}
          >
            <ChevronRight size={20} strokeWidth={2.5} />
          </div>
          <h2 className="text-lg font-bold tracking-tight text-neutral-900">
            {viewType === ChronologyViewType.VISIT_TYPE
              ? formatVisitType(template, groupKey)
              : groupLabel}
          </h2>
          <span className="rounded-lg bg-neutral-100/80 px-2.5 py-1 text-xs font-semibold uppercase tracking-micro text-neutral-500 ring-1 ring-neutral-200/50">
            {visitCount} visit{visitCount !== 1 ? "s" : ""}
          </span>
        </button>
      ) : (
        <>
          <h2 className="text-lg font-bold tracking-tight text-neutral-900">{groupLabel}</h2>
          <span className="rounded-lg bg-neutral-100/80 px-2.5 py-1 text-xs font-semibold uppercase tracking-micro text-neutral-500 ring-1 ring-neutral-200/50">
            {visitCount} visit{visitCount !== 1 ? "s" : ""}
          </span>
        </>
      )}
    </div>
  );
};
